import React, { useState } from 'react';
import { useEventListener, useIsClient } from '@fiverr-private/hooks';
import { BOOK_PUBLISHING_QUESTIONNAIRE_SHOWN_EVENT } from '@fiverr-private/book_publishing_questionnaire';
import { ROLLOUTS } from '@fiverr-private/listing_experiments';
import { useInBriefFloatingFormPreConditions } from '../../../hooks/useInBriefFloatingFormPreConditions';
import { useAppContext } from '../../context/listingsContext';
import { getBqEventSource } from '../../business_listings/biUtils';
import { getCookieValue, setCookieWithExpiration } from './BriefFloatingFormEntry.utils';
import { CLICKED_EDIT_BRIEF_COOKIE_KEY } from './BriefFloatingFormEntry.constants';
import { useBffForceOpen } from './hooks/useBffForceOpen';
import { LazyBriefFloatingFormTrigger } from './LazyBriefFloatingFormTrigger';

interface BriefFloatingFormEntryProps {
    resultsCount: number;
    observedItemIndex: number;
    isExpertListings?: boolean;
    isObservedItemSeen?: boolean;
}

export const BriefFloatingFormEntry = ({
    resultsCount,
    observedItemIndex,
    isExpertListings = false,
    isObservedItemSeen = false,
}: BriefFloatingFormEntryProps) => {
    const {
        flow,
        categoryIds,
        query,
        listingQuery,
        significantLeafCategories,
        dominateSubCategoryId,
        currency,
        appData,
        appFilters,
        userData,
        rollouts,
    } = useAppContext();
    const inCrowsMpBffExpansionRollout = !!rollouts[ROLLOUTS.CROWS_MP_BFF_EXPANSION];

    const inBffPreConditions = useInBriefFloatingFormPreConditions({
        flow,
        userType: userData?.userType,
        isExpertListings,
        resultsCount,
        inCrowsMpBffExpansionRollout,
    });

    const isClient = useIsClient();
    const [isBookPublishingQuestionnaireShown, setIsBookPublishingQuestionnaireShown] = useState(false);
    useEventListener(
        BOOK_PUBLISHING_QUESTIONNAIRE_SHOWN_EVENT,
        () => {
            setIsBookPublishingQuestionnaireShown(true);
        },
        isClient ? window : null
    );

    const shouldShowBff = inBffPreConditions && !isBookPublishingQuestionnaireShown;
    const firstPage = (appData?.pagination?.page ?? 1) === 1;
    const filtered = !!appFilters?.selectedFilters?.length;
    const clickedEditBrief = getCookieValue(CLICKED_EDIT_BRIEF_COOKIE_KEY);
    const shouldAllowForcedOpen = shouldShowBff && firstPage && !filtered && !clickedEditBrief;

    const leafCategory = categoryIds.nestedSubCategoryId || categoryIds.subCategoryId;
    const dlc = significantLeafCategories?.[0] || dominateSubCategoryId;

    const { forceOpenSettings, resetForceOpenSettings } = useBffForceOpen(shouldAllowForcedOpen, isObservedItemSeen);

    const onEditBriefClick = () => {
        setCookieWithExpiration(CLICKED_EDIT_BRIEF_COOKIE_KEY, true, 14, false);
    };

    if (!shouldShowBff) {
        return null;
    }

    return (
        <LazyBriefFloatingFormTrigger
            inCrowsMpBffExpansionRollout={inCrowsMpBffExpansionRollout}
            listingFlow={flow}
            searchQuery={query || listingQuery}
            pageName={getBqEventSource(isExpertListings)}
            currency={currency}
            forceOpen={forceOpenSettings.isForceOpen}
            afterForceOpen={forceOpenSettings.afterForceOpen}
            triggerScenario={forceOpenSettings.triggerScenario}
            onClose={resetForceOpenSettings}
            afterForceOpenRefused={resetForceOpenSettings}
            onEditBriefClick={onEditBriefClick}
            {...(isObservedItemSeen && { openingPosition: observedItemIndex + 1 })}
            {...(leafCategory && { leafCategory: Number(leafCategory) })}
            {...(dlc && { dlc: Number(dlc) })}
        />
    );
};
